import { MenuItems } from '../enums/MenuItems'
import Section from '../components/Section'
import Modal from '../components/Modal'
import networkApi from '../api/network/NetworkApi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { WorkedWithRelationshipsInfo } from '../api/network/models/WorkedWithRelationshipsInfo'
import { BackfillRelationshipScoresRes } from '../api/network/models/BackfillRelationshipScoresDtos'
import personApi from '../api/person/PersonApi'
import companyApi from '../api/company/CompanyApi'
import Tabs from '../components/Tabs'
import { useSearchParams } from 'react-router-dom'
import LayoutSingleViewFullWidth from '../uiV2/common/layout/LayoutSingleViewFullWidth'
import PersonColdStorage from '../components/dataManagement/PersonColdStorage'
import WorkHistoryColdStorage from '../components/dataManagement/WorkHistoryColdStorage'
import CompanyColdStorage from '../components/dataManagement/CompanyColdStorage'
import PersonImportQueue from '../components/dataManagement/PersonImportQueue'
import HelixSearchQueue from '../components/dataManagement/HelixSearchQueue'
import tenantApi from '../api/tenant/TenantApi'
import { BackfillReferralCodesRes } from '../api/tenant/models/BackfillReferralCodesRes'

function DataManagement(): React.JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams()
    const tabParam = searchParams.get('tab') || ''

    const tabsDef = useMemo(
        () => [
            { label: 'Summary' },
            { label: 'Person Cold Storage' },
            { label: 'Company Cold Storage' },
            { label: 'Work History Cold Storage' },
            { label: 'Person Import Queue' },
            { label: 'Helix Search Queue' },
            // { label: 'Person Graph' },
            // { label: 'Person Open Search' },
            // { label: 'Company Graph' },
        ],
        [],
    )

    const toSlug = (label: string) =>
        label
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')

    const slugToIndex = useMemo(() => {
        const m = new Map<string, number>()
        tabsDef.forEach((t, i) => m.set(toSlug(t.label), i))
        return m
    }, [tabsDef])

    const indexToSlug = useMemo(
        () => tabsDef.map((t) => toSlug(t.label)),
        [tabsDef],
    )

    const activeIndex = useMemo(() => {
        const idx = slugToIndex.get(tabParam)
        return idx !== undefined ? idx : 0
    }, [slugToIndex, tabParam])
    const [workedWithRelationshipsInfo, setWorkedWithRelationshipsInfo] =
        useState<WorkedWithRelationshipsInfo | null>(null)
    const [personCount, setPersonCount] = useState<number | null>(null)
    const [companyCount, setCompanyCount] = useState<number | null>(null)
    const [isBackfillModalOpen, setIsBackfillModalOpen] = useState(false)
    const [isBackfillRunning, setIsBackfillRunning] = useState(false)
    const [backfillResult, setBackfillResult] =
        useState<BackfillRelationshipScoresRes | null>(null)
    const [backfillError, setBackfillError] = useState<string | null>(null)
    const [isReferralBackfillRunning, setIsReferralBackfillRunning] =
        useState(false)
    const [referralBackfillResult, setReferralBackfillResult] =
        useState<BackfillReferralCodesRes | null>(null)
    const [referralBackfillError, setReferralBackfillError] = useState<
        string | null
    >(null)
    // const [importEventCount, setImportEventCount] = useState<number | null>(
    //     null,
    // )
    // const [lastImportEvent, setLastImportEvent] =
    //     useState<ImportEventDto | null>(null)

    const fetchData = useCallback(async () => {
        try {
            const personCount = await personApi.getNumberOfPersonNodes()
            setPersonCount(personCount)

            const companyCount = await companyApi.getNumberOfCompanyNodes()
            setCompanyCount(companyCount)

            // const importEventCount =
            //     await dataImportApi.getNumberOfImportEventNodes()
            // setImportEventCount(importEventCount)

            // if (importEventCount > 0) {
            //     const lastImportEvent = await dataImportApi.getLastImportEvent()
            //     setLastImportEvent(lastImportEvent)
            // } else {
            //     setLastImportEvent(null)
            // }

            const workedWithRelationshipInfo =
                await networkApi.getWorkedWithRelationshipsInfo()
            setWorkedWithRelationshipsInfo(workedWithRelationshipInfo)
        } catch (error) {
            console.error('Error fetching data:', error)
        }
    }, [])

    useEffect(() => {
        if (activeIndex === 0) {
            void fetchData()
        }
    }, [activeIndex, fetchData])

    const openBackfillModal = () => {
        setBackfillError(null)
        setIsBackfillModalOpen(true)
    }

    const closeBackfillModal = () => {
        if (isBackfillRunning) return
        setIsBackfillModalOpen(false)
        setBackfillError(null)
    }

    const handleBackfillRelationshipScores = useCallback(async () => {
        setIsBackfillRunning(true)
        setBackfillError(null)
        try {
            const result = await networkApi.backfillRelationshipScores()
            setBackfillResult(result)
            await fetchData()
            setIsBackfillModalOpen(false)
        } catch (error) {
            console.error('Error backfilling relationship scores:', error)
            setBackfillError(
                error instanceof Error
                    ? error.message
                    : 'Unexpected error while backfilling scores',
            )
        } finally {
            setIsBackfillRunning(false)
        }
    }, [fetchData])

    const handleBackfillReferralCodes = useCallback(async () => {
        setIsReferralBackfillRunning(true)
        setReferralBackfillError(null)
        try {
            const result = await tenantApi.backfillReferralCodes()
            setReferralBackfillResult(result)
        } catch (error) {
            console.error('Error backfilling referral codes:', error)
            setReferralBackfillError(
                error instanceof Error
                    ? error.message
                    : 'Unexpected error while backfilling referral codes',
            )
        } finally {
            setIsReferralBackfillRunning(false)
        }
    }, [])

    // const handleDeleteAllWorkedWithRelationships = async () => {
    //     if (
    //         confirm(
    //             'This will delete ALL the WORKED_WITH relationships.\n\nAre you sure?',
    //         )
    //     ) {
    //         setIsLoading(true)
    //         try {
    //             await networkApi.deleteWorkedWithRelationships()
    //             alert('Deleted all WORKED_WITH relationships')
    //             await fetchData()
    //         } catch (error) {
    //             console.error(error)
    //             alert('Deleting data failed')
    //         } finally {
    //             setIsLoading(false)
    //         }
    //     }
    // }

    // const handleCreateAllWorkedWithRelationships = async () => {
    //     if (
    //         confirm(
    //             'This will create ALL the WORKED_WITH relationships.\n\nAre you sure?',
    //         )
    //     ) {
    //         setIsLoading(true)
    //         try {
    //             await networkApi.createWorkedWithRelationships()
    //             alert('Created all WORKED_WITH relationships')
    //             await fetchData()
    //         } catch (error) {
    //             console.error(error)
    //             alert('Creating data failed')
    //         } finally {
    //             setIsLoading(false)
    //         }
    //     }
    // }

    // const handleProcessRoles = async () => {
    //     if (!roleId) {
    //         alert('Please enter a role ID')
    //         return
    //     }

    //     if (
    //         confirm(
    //             `This will process the role with ID: ${roleId}.\n\nAre you sure?`,
    //         )
    //     ) {
    //         setIsLoading(true)
    //         try {
    //             await roleApi.processRole({
    //                 roleId,
    //             })
    //             alert('Role processing completed successfully')
    //         } catch (error) {
    //             console.error('Error processing role:', error)
    //             alert('Role processing failed')
    //         } finally {
    //             setIsLoading(false)
    //         }
    //     }
    // }

    return (
        <LayoutSingleViewFullWidth activeMenuItem={MenuItems.DataManagement}>
            <Section title="Data Management Dashboard">
                <Tabs
                    unmountInactiveTabs
                    activeIndex={activeIndex}
                    onTabChange={(idx) => {
                        const nextSlug = indexToSlug[idx] || indexToSlug[0]
                        const newParams = new URLSearchParams(searchParams)
                        newParams.set('tab', nextSlug)
                        setSearchParams(newParams, { replace: true })
                    }}
                    tabs={[
                        {
                            label: 'Summary',
                            content: (
                                <div className="mt-6 flex flex-wrap justify-between gap-8">
                                    <Section subtitle="Summary">
                                        <div>
                                            Number of Person nodes:{' '}
                                            {personCount?.toLocaleString() ??
                                                'Loading...'}
                                        </div>
                                        <div>
                                            Number of Company nodes:{' '}
                                            {companyCount?.toLocaleString() ??
                                                'Loading...'}
                                        </div>
                                        <div>
                                            Network Generation Enabled Count:{' '}
                                            {workedWithRelationshipsInfo
                                                ? workedWithRelationshipsInfo.networkGenerationEnabledCount.toLocaleString()
                                                : 'Loading...'}
                                        </div>
                                        <div>
                                            Average WORKED_WITH per person:{' '}
                                            {workedWithRelationshipsInfo
                                                ? (Number.isFinite(
                                                      workedWithRelationshipsInfo.actual /
                                                          workedWithRelationshipsInfo.networkGenerationEnabledCount,
                                                  )
                                                      ? Math.ceil(
                                                            workedWithRelationshipsInfo.actual /
                                                                workedWithRelationshipsInfo.networkGenerationEnabledCount,
                                                        )
                                                      : 0
                                                  ).toLocaleString()
                                                : 'Loading...'}
                                        </div>
                                        <div>
                                            Average HAS_RELATIONSHIP_WITH per
                                            person:{' '}
                                            {workedWithRelationshipsInfo
                                                ? (Number.isFinite(
                                                      workedWithRelationshipsInfo.hasRelWithCount /
                                                          workedWithRelationshipsInfo.networkGenerationEnabledCount,
                                                  )
                                                      ? Math.ceil(
                                                            workedWithRelationshipsInfo.hasRelWithCount /
                                                                workedWithRelationshipsInfo.networkGenerationEnabledCount,
                                                        )
                                                      : 0
                                                  ).toLocaleString()
                                                : 'Loading...'}
                                        </div>
                                    </Section>
                                    <Section
                                        subtitle="Relationships"
                                        className="flex flex-col"
                                    >
                                        <div>
                                            WORKED_WITH count:{' '}
                                            {workedWithRelationshipsInfo
                                                ? workedWithRelationshipsInfo.actual.toLocaleString()
                                                : 'Loading...'}
                                        </div>
                                        <div>
                                            HAS_RELATIONSHIP_WITH count:{' '}
                                            {workedWithRelationshipsInfo
                                                ? workedWithRelationshipsInfo.hasRelWithCount.toLocaleString()
                                                : 'Loading...'}
                                        </div>
                                        <div className="mt-6">
                                            <div className="font-semibold">
                                                Backfill relationship scores
                                            </div>
                                            <p className="mt-1 text-sm text-gray-600">
                                                Recalculate scores for any
                                                HAS_RELATIONSHIP_WITH edges that
                                                are missing a score.
                                            </p>
                                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                                <button
                                                    onClick={openBackfillModal}
                                                    disabled={isBackfillRunning}
                                                    className="rounded bg-indigo-600 px-4 py-2 text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                                                >
                                                    {isBackfillRunning
                                                        ? 'Running...'
                                                        : 'Backfill scores'}
                                                </button>
                                                {isBackfillRunning && (
                                                    <span className="text-sm text-gray-600">
                                                        This can take a few
                                                        minutes.
                                                    </span>
                                                )}
                                            </div>
                                            {backfillError && (
                                                <div className="mt-2 text-sm text-red-600">
                                                    {backfillError}
                                                </div>
                                            )}
                                            {backfillResult && (
                                                <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                                                    <div>
                                                        Processed seed persons:{' '}
                                                        {backfillResult.processedSeedPersons.toLocaleString()}
                                                    </div>
                                                    <div>
                                                        Missing scores
                                                        before/after:{' '}
                                                        {backfillResult.relationshipsWithoutScoreBefore.toLocaleString()}{' '}
                                                        {'-> '}
                                                        {backfillResult.relationshipsWithoutScoreAfter.toLocaleString()}
                                                    </div>
                                                    <div>
                                                        Seed page size:{' '}
                                                        {backfillResult.seedPageSize.toLocaleString()}
                                                        , relationship batch:{' '}
                                                        {backfillResult.relationshipBatchSize.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Section>
                                    <Section
                                        subtitle="Referral codes"
                                        className="flex flex-col"
                                    >
                                        {referralBackfillResult && (
                                            <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                                                <div>
                                                    Users processed:{' '}
                                                    {referralBackfillResult.processed.toLocaleString()}
                                                </div>
                                                <div>
                                                    Newly generated codes:{' '}
                                                    {referralBackfillResult.newlyGenerated.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                        <div className="mt-4 flex flex-wrap items-center gap-3">
                                            <button
                                                onClick={
                                                    handleBackfillReferralCodes
                                                }
                                                disabled={
                                                    isReferralBackfillRunning
                                                }
                                                className="rounded bg-indigo-600 px-4 py-2 text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                                            >
                                                {isReferralBackfillRunning
                                                    ? 'Backfilling...'
                                                    : 'Backfill referral codes'}
                                            </button>
                                            {isReferralBackfillRunning && (
                                                <span className="text-sm text-gray-600">
                                                    Generating any missing
                                                    codes...
                                                </span>
                                            )}
                                        </div>
                                        {referralBackfillError && (
                                            <div className="mt-2 text-sm text-red-600">
                                                {referralBackfillError}
                                            </div>
                                        )}
                                    </Section>
                                </div>
                            ),
                        },
                        {
                            label: 'Person Cold Storage',
                            content: <PersonColdStorage />,
                        },
                        {
                            label: 'Company Cold Storage',
                            content: <CompanyColdStorage />,
                        },
                        {
                            label: 'Work History Cold Storage',
                            content: <WorkHistoryColdStorage />,
                        },
                        {
                            label: 'Person Import Queue',
                            content: <PersonImportQueue />,
                        },
                        {
                            label: 'Helix Search Queue',
                            content: <HelixSearchQueue />,
                        },
                        // {
                        //     label: 'Person Graph',
                        //     content: (
                        //         <PersonGraph />
                        //     ),
                        // },
                        // {
                        //     label: 'Person Open Search',
                        //     content: (
                        //         <PersonOpenSearch />
                        //     ),
                        // },
                        // {
                        //     label: 'Company Graph',
                        //     content: (
                        //         <CompanyGraph />
                        //     ),
                        // },
                    ]}
                />
            </Section>
            {isBackfillModalOpen && (
                <Modal
                    title="Backfill relationship scores"
                    onClose={closeBackfillModal}
                    allowUserToClose={!isBackfillRunning}
                    leftFooter={
                        backfillError ? (
                            <span className="text-sm text-red-600">
                                {backfillError}
                            </span>
                        ) : undefined
                    }
                    rightFooter={
                        <>
                            <button
                                type="button"
                                onClick={closeBackfillModal}
                                disabled={isBackfillRunning}
                                className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleBackfillRelationshipScores}
                                disabled={isBackfillRunning}
                                className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                            >
                                {isBackfillRunning
                                    ? 'Running...'
                                    : 'Run backfill'}
                            </button>
                        </>
                    }
                >
                    <p className="text-sm text-gray-800">
                        This will recalculate scores for any
                        HAS_RELATIONSHIP_WITH edges that are missing a score.
                        Existing scores are left untouched.
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                        Seeds are fetched in batches of 500 and relationships
                        are scored in batches of 2,000. The operation runs
                        immediately and can take a few minutes.
                    </p>
                </Modal>
            )}
        </LayoutSingleViewFullWidth>
    )
}

export default DataManagement
